import React, { useState } from 'react';
import { University } from '../../../types';

interface StatisticsTabProps {
  university: University;
}

const StatisticsTab: React.FC<StatisticsTabProps> = ({ university }) => {
  const [satScore, setSatScore] = useState<string>('');
  const [actScore, setActScore] = useState<string>('');
  const [calculatedPercentile, setCalculatedPercentile] = useState<number | null>(null);
  const [userScorePosition, setUserScorePosition] = useState<number | null>(null);

  // Get admission status based on acceptance rate
  const getAdmissionStatus = () => {
    const rate = university.acceptance_rate;
    if (rate < 20) return { status: 'Reach', description: 'Most Competitive', color: 'text-orange-600' };
    if (rate < 40) return { status: 'Target', description: 'Somewhat Competitive', color: 'text-blue-600' };
    return { status: 'Safety', description: 'Less Competitive', color: 'text-green-600' };
  };

  // Get selectivity label based on acceptance rate
  const getSelectivityLabel = () => {
    const rate = university.acceptance_rate;
    if (rate < 20) return 'Most Selective';
    if (rate < 50) return 'Somewhat Selective';
    return 'Less Selective';
  };

  // Calculate percentile based on score using university-specific quartile ranges
  const calculatePercentile = (score: number, isACT: boolean = false) => {
    if (isACT) {
      // Use university's ACT range if available, otherwise fall back to national averages
      if (university.act_range_min && university.act_range_max) {
        const q1 = university.act_range_min;
        const q3 = university.act_range_max;
        const mean = (q1 + q3) / 2;
        
        if (score <= q1) {
          const minScore = 1;
          if (score <= minScore) {
            return 1;
          }
          const scoreRange = q1 - minScore;
          const percentileRange = 24;
          const ratio = (score - minScore) / scoreRange;
          const estimatedPercentile = 1 + (ratio * percentileRange);
          return Math.round(estimatedPercentile);
        } else if (score >= q3) {
          const range = q3 - q1;
          const stdDev = range / 2;
          const pointsAboveQ3 = score - q3;
          const stdDevsAboveQ3 = pointsAboveQ3 / stdDev;
          
          const estimatedPercentile = 75 + (stdDevsAboveQ3 * 12);
          return Math.round(Math.min(99, estimatedPercentile));
        } else if (score <= mean) {
          const ratio = (score - q1) / (mean - q1);
          return Math.round(25 + ratio * 25);
        } else {
          const ratio = (score - mean) / (q3 - mean);
          return Math.round(50 + ratio * 25);
        }
      } else {
        const mean = 21;
        const stdDev = 5;
        const zScore = (score - mean) / stdDev;
        return Math.max(1, Math.min(99, Math.round(50 + (zScore * 34.13))));
      }
    } else {
      if (university.sat_range_min && university.sat_range_max) {
        const q1 = university.sat_range_min;
        const q3 = university.sat_range_max;
        const mean = (q1 + q3) / 2;
        
        if (score <= q1) {
          const minScore = 400;
          if (score <= minScore) {
            return 1;
          }
          const scoreRange = q1 - minScore;
          const percentileRange = 24;
          const ratio = (score - minScore) / scoreRange;
          const estimatedPercentile = 1 + (ratio * percentileRange);
          return Math.round(estimatedPercentile);
        } else if (score >= q3) {
          const range = q3 - q1;
          const stdDev = range / 2;
          const pointsAboveQ3 = score - q3;
          const stdDevsAboveQ3 = pointsAboveQ3 / stdDev;
          const estimatedPercentile = 75 + (stdDevsAboveQ3 * 12);
          return Math.round(Math.min(99, estimatedPercentile));
        } else if (score <= mean) {
          const ratio = (score - q1) / (mean - q1);
          return Math.round(25 + ratio * 25);
        } else {
          const ratio = (score - mean) / (q3 - mean);
          return Math.round(50 + ratio * 25);
        }
      } else {
        const mean = 1060;
        const stdDev = 210;
        const zScore = (score - mean) / stdDev;
        return Math.max(1, Math.min(99, Math.round(50 + (zScore * 34.13))));
      }
    }
  };

  // Calculate position on bell curve (for SVG positioning)
  const calculatePosition = (score: number, isACT: boolean = false) => {
    if (isACT) {
      if (university.act_range_min && university.act_range_max) {
        const q1 = university.act_range_min;
        const q3 = university.act_range_max;
        const range = q3 - q1;
        const min = q1 - range;
        const max = q3 + range;
        return 50 + ((score - min) / (max - min)) * 300;
      } else {
        // ACT score (1-36) to SVG position (50-350)
        return 50 + ((score - 1) / 35) * 300;
      }
    } else {
      if (university.sat_range_min && university.sat_range_max) {
        const q1 = university.sat_range_min;
        const q3 = university.sat_range_max;
        const range = q3 - q1;
        const min = q1 - range;
        const max = q3 + range;
        return 50 + ((score - min) / (max - min)) * 300;
      } else {
        // SAT score (400-1600) to SVG position (50-350)
        return 50 + ((score - 400) / 1200) * 300;
      }
    }
  };

  const handleCalculatePercentile = () => {
    const sat = parseInt(satScore);
    const act = parseInt(actScore);

    if (!sat && !act) {
      alert('Please enter either an SAT or ACT score');
      return;
    }

    let percentile = 0;
    let position = 0;

    if (sat && sat >= 400 && sat <= 1600) {
      percentile = calculatePercentile(sat, false);
      position = calculatePosition(sat, false);
    } else if (act && act >= 1 && act <= 36) {
      percentile = calculatePercentile(act, true);
      position = calculatePosition(act, true);
    } else {
      alert('Please enter a valid score range (SAT: 400-1600, ACT: 1-36)');
      return;
    }

    setCalculatedPercentile(percentile);
    setUserScorePosition(position);
  };

  const admissionStatus = getAdmissionStatus();

  return (
    <div className="tab-content">
      <h3 className="text-3xl font-bold mb-8" style={{ color: 'var(--text-primary)' }}>Admission Statistics</h3>
      
      {/* Header Information */}
      <div className="border rounded-xl p-6 mb-8" style={{ background: 'var(--surface-primary)', borderColor: 'var(--border-primary)' }}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <div className="text-red-500 text-2xl mr-3">
              <i className="fas fa-chart-bar"></i>
            </div>
            <h4 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>Admission Overview</h4>
          </div>
          <a 
            href={university.admissions_website?.startsWith('http') ? university.admissions_website : `https://${university.admissions_website}`} 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-blue-600 hover:text-blue-800 font-medium text-sm"
          >
            Visit Website →
          </a>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="text-center p-4 bg-red-50/50 rounded-lg">
            <h5 className="font-semibold mb-2" style={{ color: 'var(--text-secondary)' }}>Acceptance Rate</h5>
            <p className="text-3xl font-bold text-red-600">{university.acceptance_rate}%</p>
            <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{getSelectivityLabel()}</p>
          </div>
          <div className="text-center p-4 bg-orange-50/50 rounded-lg">
            <h5 className="font-semibold mb-2" style={{ color: 'var(--text-secondary)' }}>Admission Status</h5>
            <p className={`text-2xl font-bold ${admissionStatus.color}`}>{admissionStatus.status}</p>
            <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{admissionStatus.description}</p>
          </div>
        </div>
      </div>

      {/* Test Score Ranges */}
      <div className="border rounded-xl p-6 mb-8" style={{ background: 'var(--surface-primary)', borderColor: 'var(--border-primary)' }}>
        <div className="flex items-center mb-6">
          <div className="text-blue-500 text-2xl mr-3">
            <i className="fas fa-chart-line"></i>
          </div>
          <h4 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>Test Score Ranges</h4>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="text-center p-4 bg-blue-50/50 rounded-lg">
            <h5 className="font-semibold mb-2" style={{ color: 'var(--text-secondary)' }}>SAT Range</h5>
            <p className="text-2xl font-bold text-blue-600">
              {university.sat_range_min && university.sat_range_max 
                ? `${university.sat_range_min}-${university.sat_range_max}`
                : 'N/A'
              }
            </p>
            <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>25th-75th percentile</p>
          </div>
          <div className="text-center p-4 bg-blue-50/50 rounded-lg">
            <h5 className="font-semibold mb-2" style={{ color: 'var(--text-secondary)' }}>ACT Range</h5>
            <p className="text-2xl font-bold text-blue-600">
              {university.act_range_min && university.act_range_max 
                ? `${university.act_range_min}-${university.act_range_max}`
                : 'N/A'
              }
            </p>
            <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>25th-75th percentile</p>
          </div>
        </div>
      </div>

      {/* Test Score Calculator */}
      <div className="border rounded-xl p-6" style={{ background: 'var(--surface-primary)', borderColor: 'var(--border-primary)' }}>
        <h4 className="text-lg font-bold mb-6" style={{ color: 'var(--text-primary)' }}>Test Score Percentile Calculator</h4>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Input Section */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-secondary)' }}>Enter Your SAT Score</label>
              <input 
                type="number" 
                placeholder="Enter SAT score (400-1600)" 
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" style={{ borderColor: 'var(--border-primary)' }}
                value={satScore}
                onChange={(e) => setSatScore(e.target.value)}
                min="400"
                max="1600"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-secondary)' }}>Enter Your ACT Score</label>
              <input 
                type="number" 
                placeholder="Enter ACT score (1-36)" 
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" style={{ borderColor: 'var(--border-primary)' }}
                value={actScore}
                onChange={(e) => setActScore(e.target.value)}
                min="1"
                max="36"
              />
            </div>
            <button 
              onClick={handleCalculatePercentile}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
            >
              Calculate Percentile
            </button>
            
            {/* Results */}
            {calculatedPercentile !== null && (
              <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                <h5 className="font-semibold text-blue-900 mb-2">Your Percentile</h5>
                <p className="text-2xl font-bold text-blue-600">{calculatedPercentile}th percentile</p>
              </div>
            )}
          </div>

          {/* Bell Curve Visualization */}
          <div className="relative">
            <h5 className="font-semibold mb-4 text-center" style={{ color: 'var(--text-secondary)' }}>Test Score Distribution</h5>
            <div className="relative h-64 rounded-lg border-2 flex items-end justify-center" style={{ background: 'var(--surface-hover)', borderColor: 'var(--border-primary)' }}>
              {/* Bell curve SVG */}
              <svg className="w-full h-full" viewBox="0 0 400 200">
                {/* Bell curve path */}
                <path
                  d="M 50 180 Q 100 50 200 80 Q 300 50 350 180"
                  stroke="var(--info-light)"
                  strokeWidth="3"
                  fill="none"
                />
                
                {/* Mean line */}
                <line
                  x1="200" y1="80" x2="200" y2="180"
                  stroke="var(--danger-primary)"
                  strokeWidth="2"
                  strokeDasharray="5,5"
                />
                <text x="200" y="75" textAnchor="middle" className="text-xs fill-red-600 font-semibold">Mean</text>
                
                {/* Standard deviation lines */}
                <line 
                  x1="150" y1="120" x2="150" y2="180" 
                  stroke="var(--success-primary)" 
                  strokeWidth="1" 
                  strokeDasharray="3,3"
                />
                <text x="150" y="115" textAnchor="middle" className="text-xs fill-green-600 font-semibold">-1σ</text>
                
                <line 
                  x1="250" y1="120" x2="250" y2="180" 
                  stroke="var(--success-primary)" 
                  strokeWidth="1" 
                  strokeDasharray="3,3"
                />
                <text x="250" y="115" textAnchor="middle" className="text-xs fill-green-600 font-semibold">+1σ</text>
                
                {/* User score marker */}
                {userScorePosition !== null && (
                  <>
                    <circle
                      cx={userScorePosition}
                      cy={160 - (calculatedPercentile! / 100) * 80}
                      r="6"
                      fill="var(--warning-primary)"
                      stroke="var(--warning-dark)"
                      strokeWidth="2"
                    />
                    <text 
                      x={userScorePosition} 
                      y={160 - (calculatedPercentile! / 100) * 80 - 10} 
                      textAnchor="middle" 
                      className="text-xs fill-amber-600 font-semibold"
                    >
                      Your Score
                    </text>
                  </>
                )}
              </svg>
              
              <div className="absolute bottom-2 left-4 text-xs" style={{ color: 'var(--text-secondary)' }}>
                {(satScore && university.sat_range_min && university.sat_range_max) ? (
                  <>
                    <div>{university.sat_range_min - (university.sat_range_max - university.sat_range_min)}</div>
                    <div className="mt-8">{university.sat_range_min}</div>
                    <div className="mt-8">{Math.round((university.sat_range_min + university.sat_range_max) / 2)}</div>
                  </>
                ) : (actScore && university.act_range_min && university.act_range_max) ? (
                  <>
                    <div>{university.act_range_min - (university.act_range_max - university.act_range_min)}</div>
                    <div className="mt-8">{university.act_range_min}</div>
                    <div className="mt-8">{Math.round((university.act_range_min + university.act_range_max) / 2)}</div>
                  </>
                ) : (
                  <>
                    <div>1200</div>
                    <div className="mt-8">1300</div>
                    <div className="mt-8">1400</div>
                  </>
                )}
              </div>
              <div className="absolute bottom-2 right-4 text-xs" style={{ color: 'var(--text-secondary)' }}>
                {(satScore && university.sat_range_min && university.sat_range_max) ? (
                  <>
                    <div>{university.sat_range_max}</div>
                    <div className="mt-8">{university.sat_range_max + (university.sat_range_max - university.sat_range_min)}</div>
                  </>
                ) : (actScore && university.act_range_min && university.act_range_max) ? (
                  <>
                    <div>{university.act_range_max}</div>
                    <div className="mt-8">{university.act_range_max + (university.act_range_max - university.act_range_min)}</div>
                  </>
                ) : (
                  <>
                    <div>1500</div>
                    <div className="mt-8">1600</div>
                  </>
                )}
              </div>
            </div>
            
            {/* Legend */}
            <div className="mt-4 space-y-2 text-sm">
              <div className="flex items-center">
                <div className="w-4 h-0.5 bg-red-500 mr-2"></div>
                <span style={{ color: 'var(--text-secondary)' }}>
                  Mean ({
                    (satScore && university.sat_range_min && university.sat_range_max) 
                      ? Math.round((university.sat_range_min + university.sat_range_max) / 2)
                      : (actScore && university.act_range_min && university.act_range_max)
                        ? Math.round((university.act_range_min + university.act_range_max) / 2)
                        : 1345
                  })
                </span>
              </div>
              <div className="flex items-center">
                <div className="w-4 h-0.5 bg-green-500 mr-2" style={{borderTop: '1px dashed var(--success-primary)'}}></div>
                <span style={{ color: 'var(--text-secondary)' }}>
                  Standard Deviation (±{
                    (satScore && university.sat_range_min && university.sat_range_max) 
                      ? Math.round((university.sat_range_max - university.sat_range_min) / 2)
                      : (actScore && university.act_range_min && university.act_range_max)
                        ? Math.round((university.act_range_max - university.act_range_min) / 2)
                        : 105
                  })
                </span>
              </div>
              <div className="flex items-center">
                <div className="w-3 h-3 bg-amber-500 rounded-full mr-2"></div>
                <span style={{ color: 'var(--text-secondary)' }}>Your Score</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StatisticsTab;
